import React, { useState, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { MapPin, Calendar, AlertTriangle, ArrowUp, ArrowDown, Info, Search, X } from 'lucide-react';

const TraceabilityTable = ({ segments, loading, error, onPageChange, totalItems: serverTotalItems, initialPage = 1, pageSize = 30 }) => {
    const [sortOrder, setSortOrder] = useState('desc');
    const [activeTooltip, setActiveTooltip] = useState(null);
    const [localPage, setLocalPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const ITEMS_PER_PAGE = pageSize;

    // Usar página del servidor si se proporciona (vía control onPageChange), sino usar local
    const currentPage = onPageChange ? initialPage : localPage;
    const isServerSide = !!onPageChange;

    const handleTooltipEnter = (e, content, index) => {
        const rect = e.currentTarget.getBoundingClientRect();
        const spaceBelow = window.innerHeight - rect.bottom;
        let position = 'top';
        let y = rect.bottom + 10;
        if (spaceBelow < 120) {
            position = 'bottom';
            y = rect.top - 10;
        }
        setActiveTooltip({
            x: rect.left + rect.width / 2,
            y: y,
            content: content,
            position: position
        });
    };

    const handleTooltipLeave = () => {
        setActiveTooltip(null);
    };

    const sortedSegments = useMemo(() => {
        if (!segments) return [];
        return [...segments].sort((a, b) => {
            if (sortOrder === 'asc') {
                return a.id - b.id;
            } else {
                return b.id - a.id;
            }
        });
    }, [segments, sortOrder]);

    // Reiniciar página local cuando cambian los datos o el término de búsqueda
    React.useEffect(() => {
        if (!isServerSide) setLocalPage(1);
    }, [segments, isServerSide, searchTerm]);

    // Filtrar segmentos por VIN si hay término de búsqueda (Solo lado cliente por ahora, o local a la lista actual)
    const filteredSegments = useMemo(() => {
        if (!searchTerm) return sortedSegments;
        return sortedSegments.filter(segment =>
            (segment.vin || segment.vehiculo?.vin || '').toLowerCase().includes(searchTerm.toLowerCase())
        );
    }, [sortedSegments, searchTerm]);

    // Calcular totales
    const totalCount = isServerSide ? (serverTotalItems || 0) : filteredSegments.length;
    const totalPages = Math.ceil(totalCount / ITEMS_PER_PAGE);

    // Cortar para lado cliente, pasar directo para lado servidor
    const currentSegments = useMemo(() => {
        if (isServerSide) return filteredSegments; // Mostrar todos los items obtenidos (la página es manejada por API)
        const start = (localPage - 1) * ITEMS_PER_PAGE;
        return filteredSegments.slice(start, start + ITEMS_PER_PAGE);
    }, [filteredSegments, localPage, isServerSide, ITEMS_PER_PAGE]);

    const handlePrevious = () => {
        if (isServerSide) {
            if (currentPage > 1) onPageChange(currentPage - 1);
        } else {
            setLocalPage(prev => Math.max(prev - 1, 1));
        }
    };

    const handleNext = () => {
        if (isServerSide) {
            if (currentPage < totalPages) onPageChange(currentPage + 1);
        } else {
            setLocalPage(prev => Math.min(prev + 1, totalPages));
        }
    };

    const toggleSort = () => {
        setSortOrder(current => current === 'asc' ? 'desc' : 'asc');
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        try {
            const date = new Date(dateString);
            return date.toLocaleString('es-MX', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
                hour12: true
            });
        } catch (e) {
            return dateString;
        }
    };

    const calculateDuration = (start, end) => {
        if (!start || !end) return '-';
        const startDate = new Date(start);
        const endDate = new Date(end);
        const diff = endDate - startDate;

        if (diff < 0) return '0 minutos';

        const hours = Math.floor(diff / (1000 * 60 * 60));
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

        if (hours === 0) {
            return `${minutes} minutos`;
        }

        return `${hours} hrs y ${minutes} min`;
    };

    const getLocationName = (loc) => {
        if (!loc) return '-';
        return loc.patio_nombre || loc.ubicacion || loc.nombre || '-';
    };

    return (
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm flex flex-col min-h-full overflow-hidden">
            <div className="flex-1 flex flex-col min-h-0 w-full">
                {loading ? (
                    <div className="flex justify-center items-center py-20">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    </div>
                ) : error ? (
                    <div className="flex flex-col items-center justify-center py-12 text-center">
                        <div className="mb-4">
                            <AlertTriangle className="w-16 h-16 text-gray-300" strokeWidth={1} />
                        </div>
                        <h3 className="text-lg font-medium text-gray-900 mb-1">
                            No se pudo cargar la información
                        </h3>
                        <p className="text-gray-500">
                            {error}
                        </p>
                    </div>
                ) : (
                    <>
                        {/* Barra de Búsqueda VIN */}
                        <div className="px-4 py-3 border-b border-gray-100 bg-gray-50/50">
                            <div className="relative max-w-sm">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <Search className="h-4 w-4 text-gray-400" />
                                </div>
                                <input
                                    type="text"
                                    className="block w-full pl-10 pr-10 py-2 border border-gray-200 rounded-lg leading-5 bg-white placeholder-gray-400 focus:outline-none focus:placeholder-gray-300 focus:border-blue-300 focus:ring focus:ring-blue-200 sm:text-sm transition duration-150 ease-in-out"
                                    placeholder="Buscar por VIN..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                                {searchTerm && (
                                    <button
                                        onClick={() => setSearchTerm('')}
                                        className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 transition-colors"
                                    >
                                        <X className="h-4 w-4" />
                                    </button>
                                )}
                            </div>
                        </div>

                        <div className="flex-1 relative overflow-x-auto -mx-px">
                            <table className="min-w-[600px] w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th
                                            className="px-3 py-2 text-left text-[10px] font-medium text-gray-500 uppercase tracking-wider bg-gray-50 cursor-pointer hover:bg-gray-100 transition-colors select-none group w-12"
                                            onClick={toggleSort}
                                        >
                                            <div className="flex items-center gap-1">
                                                #
                                                {sortOrder === 'asc' ?
                                                    <ArrowUp className="w-3 h-3 text-blue-600" /> :
                                                    <ArrowDown className="w-3 h-3 text-blue-600" />
                                                }
                                            </div>
                                        </th>
                                        <th className="px-3 py-2 text-left text-[10px] font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                                            Vehículo
                                        </th>
                                        <th className="px-3 py-2 text-left text-[10px] font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                                            Origen
                                        </th>
                                        <th className="px-3 py-2 text-left text-[10px] font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                                            Destino
                                        </th>
                                        <th className="px-3 py-2 text-center text-[10px] font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                                            Distancia
                                        </th>
                                        <th className="px-3 py-2 text-center text-[10px] font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                                            Consumo
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {currentSegments.map((segment, index) => (
                                        <tr key={index} className="hover:bg-gray-50 transition-colors">
                                            <td className="px-3 py-3 whitespace-nowrap text-xs text-gray-400 font-medium align-top">
                                                {segment.id}
                                            </td>
                                            <td className="px-3 py-3">
                                                <div className="flex flex-col gap-0.5 min-w-[100px]">
                                                    <span className="text-xs font-bold text-gray-900 font-mono tracking-tight leading-tight">
                                                        {segment.vin || segment.vehiculo?.vin || 'N/A'}
                                                    </span>
                                                    <div className="flex flex-col text-[10px] text-gray-500 leading-tight">
                                                        <div className="flex items-baseline gap-1">
                                                            <span className="font-semibold truncate max-w-[80px]" title={segment.vehiculo?.familia}>
                                                                {segment.vehiculo?.familia || '-'}
                                                            </span>
                                                            <span className="text-gray-400">
                                                                {segment.vehiculo?.ano_modelo || '-'}
                                                            </span>
                                                        </div>
                                                        <span className="text-gray-400 truncate tracking-wide">
                                                            {segment.vehiculo?.catalogo || '-'}
                                                        </span>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-3 py-3">
                                                <div className="flex flex-col gap-1">
                                                    <div className="flex flex-wrap items-center gap-1.5">
                                                        <MapPin className="w-3 h-3 text-gray-400 shrink-0" />
                                                        <span className="text-xs font-semibold text-gray-900 leading-tight break-words">
                                                            {getLocationName(segment.origen)}
                                                        </span>
                                                        <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold border shrink-0 ${segment.origen?.tipo_lectura === 'antena'
                                                            ? 'bg-purple-50 text-purple-700 border-purple-100'
                                                            : 'bg-blue-50 text-blue-700 border-blue-100'
                                                            }`}>
                                                            {segment.origen?.tipo_lectura}
                                                        </span>
                                                    </div>
                                                    <div className="text-[10px] text-gray-500 flex items-center pl-4.5">
                                                        <Calendar className="w-3 h-3 mr-1 opacity-70" />
                                                        {formatDate(segment.origen?.fecha)}
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-3 py-3">
                                                <div className="flex flex-col gap-1">
                                                    <div className="flex flex-wrap items-center gap-1.5">
                                                        <MapPin className="w-3 h-3 text-gray-400 shrink-0" />
                                                        <span className="text-xs font-semibold text-gray-900 leading-tight break-words">
                                                            {getLocationName(segment.destino)}
                                                        </span>
                                                        <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold border shrink-0 ${segment.destino?.tipo_lectura === 'antena'
                                                            ? 'bg-purple-50 text-purple-700 border-purple-100'
                                                            : 'bg-blue-50 text-blue-700 border-blue-100'
                                                            }`}>
                                                            {segment.destino?.tipo_lectura}
                                                        </span>
                                                    </div>
                                                    <div className="text-[10px] text-gray-500 flex items-center pl-4.5">
                                                        <Calendar className="w-3 h-3 mr-1 opacity-70" />
                                                        {formatDate(segment.destino?.fecha)}
                                                    </div>
                                                </div>
                                            </td>
                                            {(!segment.distancia_km || segment.distancia_km === 0) && segment.origen?.patio_nombre !== segment.destino?.patio_nombre ? (
                                                <td colSpan="2" className="px-3 py-3 text-center align-middle">
                                                    <div
                                                        className="relative group flex justify-center"
                                                        onMouseEnter={(e) => handleTooltipEnter(e, "No se ha configurado la distancia entre estos sitios", index)}
                                                        onMouseLeave={handleTooltipLeave}
                                                    >
                                                        <div className="bg-orange-50 p-1.5 rounded-full cursor-help hover:bg-orange-100 transition-colors inline-block">
                                                            <AlertTriangle className="w-4 h-4 text-orange-500" />
                                                        </div>
                                                    </div>
                                                </td>
                                            ) : segment.origen?.patio_nombre === segment.destino?.patio_nombre ? (
                                                <td colSpan="2" className="px-3 py-3 text-center align-middle">
                                                    <div className="flex justify-center">
                                                        <div className="inline-flex items-center px-2 py-1 rounded-md bg-blue-50 border border-blue-100 text-blue-700 text-[10px] font-medium max-w-full">
                                                            <Info className="w-3 h-3 mr-1.5 shrink-0" />
                                                            <span className="truncate">
                                                                Mismo sitio • {calculateDuration(segment.origen?.fecha, segment.destino?.fecha)}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </td>
                                            ) : (
                                                <>
                                                    <td className="px-3 py-3 whitespace-nowrap text-xs text-gray-600 font-medium text-center align-middle">
                                                        {segment.distancia_km > 0 ? `${segment.distancia_km.toFixed(1)} km` : '-'}
                                                    </td>
                                                    <td className="px-3 py-3 whitespace-nowrap text-xs text-gray-600 font-medium text-center align-middle">
                                                        {segment.litros_consumidos > 0 ? `${segment.litros_consumidos.toFixed(2)} L` : '-'}
                                                    </td>
                                                </>
                                            )}
                                        </tr>
                                    ))}
                                    {(!currentSegments || currentSegments.length === 0) && (
                                        <tr>
                                            <td colSpan="6" className="px-6 py-10 text-center text-sm text-gray-500">
                                                {searchTerm ? 'No se encontraron resultados para la búsqueda.' : 'No hay registros disponibles.'}
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {/* Pie de página de paginación - Mostrar solo si hay items */}
                        {filteredSegments.length > 0 && (
                            <div className="flex flex-wrap items-center justify-between gap-2 px-3 sm:px-4 py-3 border-t border-gray-100 bg-gray-50 rounded-b-lg">
                                <div className="text-[10px] sm:text-xs text-gray-500">
                                    Mostrando <span className="font-semibold">{(currentPage - 1) * ITEMS_PER_PAGE + 1}</span> a <span className="font-semibold">{Math.min(currentPage * ITEMS_PER_PAGE, totalCount)}</span> de <span className="font-semibold">{totalCount}</span> resultados
                                </div>
                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={handlePrevious}
                                        disabled={currentPage === 1}
                                        className="px-3 py-1 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        Anterior
                                    </button>
                                    <span className="text-xs text-gray-600">
                                        Pág {currentPage} de {totalPages}
                                    </span>
                                    <button
                                        onClick={handleNext}
                                        disabled={currentPage === totalPages}
                                        className="px-3 py-1 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        Siguiente
                                    </button>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* Fixed Tooltip Portal */}
            {/* Portal de Tooltip Fijo */}
            {activeTooltip && createPortal(
                <div
                    className="fixed z-[100] w-56 bg-gray-800 text-white text-xs rounded-lg p-2 shadow-lg text-center whitespace-normal pointer-events-none transition-opacity duration-150"
                    style={{
                        left: activeTooltip.x,
                        top: activeTooltip.position === 'top' ? activeTooltip.y : undefined,
                        bottom: activeTooltip.position === 'bottom' ? window.innerHeight - activeTooltip.y : undefined,
                        transform: 'translateX(-50%)'
                    }}
                >
                    <div
                        className={`absolute left-1/2 -translate-x-1/2 w-2 h-2 bg-gray-800 rotate-45 ${activeTooltip.position === 'top' ? '-top-1' : '-bottom-1'
                            }`}
                    ></div>
                    {activeTooltip.content}
                </div>,
                document.body
            )}
        </div>
    );
};

export default TraceabilityTable;